import { Event, Component, h, Element, Prop, Watch, EventEmitter } from '@stencil/core';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { IKeenSliderOptions } from './rzf-carousel.interfaces';
import 'keen-slider/keen-slider.min.css';

@Component({
  tag: 'rzf-carousel',
  styleUrl: 'rzf-carousel.css',
  shadow: true,
})
export class RzfCarousel {
  @Event() progressBarEvent: EventEmitter<{
    current: number;
    total: number;
  }>;

  @Event() carouselChanged: EventEmitter<{
    currentSlide: number;
    nextId?: number;
    direction?: 'R' | 'L';
  }>;

  @Element() el: HTMLElement;
  @Prop() config: IKeenSliderOptions = {
    pagination: false,
    navigation: true,
    showProgressBar: false,
  };

  private mainSlider: KeenSliderInstance | null = null;
  private contentSlider: KeenSliderInstance | null = null;

  @Watch('config')
  configChanged(newConfig: IKeenSliderOptions) {
    this.setGridClass(newConfig);
    this.setupPagination();
  }

  componentDidLoad() {
    const mainSliderElement = this.el.querySelector('div[slot=slider]') as HTMLElement;
    const contentSliderElement = this.el.querySelector('div[slot=content]') as HTMLElement;

    if (!mainSliderElement) {
      console.warn('Main slider element is not set properly');
      return;
    }

    this.mainSlider = new KeenSlider(mainSliderElement, this.config);

    if (contentSliderElement) {
      this.contentSlider = new KeenSlider(contentSliderElement, {
        touch: true,
        loop: true,
        drag: true,
        slides: { origin: 'center', perView: 1, spacing: 0 },
      } as IKeenSliderOptions);

      this.mainSlider.on('slideChanged', slider => {
        if (this.contentSlider) {
          this.contentSlider.moveToIdx(slider.track.details.rel);
        }
        this.updateDotState(slider.track.details.rel);
      });

      this.contentSlider.on('slideChanged', slider => {
        if (this.mainSlider) {
          this.mainSlider.moveToIdx(slider.track.details.rel);
        }
        this.updateDotState(slider.track.details.rel);
      });
    } else {
      this.mainSlider.on('slideChanged', slider => {
        this.updateDotState(slider.track.details.rel);
      });

      const contentSlider = this.el.querySelector('.rzf-carousel__content-slider') as HTMLElement;
      contentSlider.classList.add;
    }

    this.setupNavigation();
    this.setupPagination();
    this.setupProgressBar();
    this.setGridClass(this.config);
  }

  setupProgressBar() {
    if (this.config.showProgressBar && this.mainSlider) {
      this.mainSlider.on('slideChanged', slider => {
        this.progressBarEvent.emit({
          current: slider.track.details.rel,
          total: slider.track.details.slides.length,
        });
      });
    }
  }

  setupPagination() {
    if (this.config.pagination && this.mainSlider) {
      const wrapper = this.el.shadowRoot.querySelector('.rzf-carousel__dots') as HTMLElement;
      if (wrapper) {
        wrapper.innerHTML = '';
        this.mainSlider.track.details.slides.forEach((_e, idx) => {
          const dot = this.createDiv('rzf-carousel__dot');

          if (idx === 0) {
            dot.classList.add('rzf-carousel__dot--active');
          }

          dot.addEventListener('click', () => {
            this.mainSlider.moveToIdx(idx);
            this.carouselChanged.emit({ currentSlide: idx, nextId: idx });
            if (this.contentSlider) {
              this.contentSlider.moveToIdx(idx);
            }
            this.updateDotState(idx);
          });
          wrapper.append(dot);
        });
      }
    }
  }

  setupNavigation() {
    const nextButton = this.el.shadowRoot.querySelector('.keen-slider__next') as HTMLElement;
    const prevButton = this.el.shadowRoot.querySelector('.keen-slider__prev') as HTMLElement;

    if (this.config.navigation && nextButton) {
      nextButton.addEventListener('click', () => {
        this.mainSlider.next();
        this.carouselChanged.emit({ currentSlide: this.mainSlider.track.details.rel, direction: 'R' });
        if (this.contentSlider) {
          this.contentSlider.next();
        }
        if (this.config.pagination) this.changeDotState(undefined, 'next');
      });
    }

    if (this.config.navigation && prevButton) {
      prevButton.addEventListener('click', () => {
        this.mainSlider.prev();
        this.carouselChanged.emit({ currentSlide: this.mainSlider.track.details.rel, direction: 'L' });
        if (this.contentSlider) {
          this.contentSlider.prev();
        }
        if (this.config.pagination) this.changeDotState(undefined, 'prev');
      });
    }
  }

  changeDotState(idx?: number, direction: 'prev' | 'next' = 'next') {
    let id = idx;
    const dots = this.el.shadowRoot.querySelectorAll('.rzf-carousel__dot');
    if (id === undefined) {
      let i = 0;
      dots.forEach(item => {
        if (item.classList.contains('rzf-carousel__dot--active')) {
          if (direction === 'next') {
            id = i + 1 >= dots.length ? 0 : i + 1;
          }
          if (direction === 'prev') {
            id = i - 1 < 0 ? dots.length - 1 : i - 1;
          }
        }
        i += 1;
      });
    }

    dots.forEach(item => {
      item.classList.remove('rzf-carousel__dot--active');
    });
    if (id !== undefined) {
      dots[id].classList.add('rzf-carousel__dot--active');
    }
  }

  updateDotState(index: number) {
    const dots = this.el.shadowRoot.querySelectorAll('.rzf-carousel__dot');
    dots.forEach((dot, idx) => {
      dot.classList.toggle('rzf-carousel__dot--active', idx === index);
    });
  }

  setGridClass(config: IKeenSliderOptions) {
    const classMapping = {
      'true-true-true': 'rzf-carousel--full',
      'true-true-false': 'rzf-carousel--no-dots',
      'false-true-true': 'rzf-carousel--no-navigation',
      'true-false-true': 'rzf-carousel--no-progress-bar',
      'true-false-false': 'rzf-carousel--navigation-only',
      'false-true-false': 'rzf-carousel--progress-bar-only',
      'false-false-true': 'rzf-carousel--dots-only',
      'false-false-false': 'rzf-carousel--basic',
    };

    const key = `${config.navigation}-${config.showProgressBar}-${config.pagination}`;
    const carouselElement = this.el.shadowRoot.querySelector('.rzf-carousel');

    if (carouselElement) {
      carouselElement.classList.add(classMapping[key] || 'rzf-carousel--basic');
    }
  }

  disconnectedCallback() {
    if (this.mainSlider) this.mainSlider.destroy();
    if (this.contentSlider) this.contentSlider.destroy();
  }

  createDiv(className: string) {
    const div = document.createElement('div');
    className.split(' ').forEach(name => div.classList.add(name));
    return div;
  }

  render() {
    return (
      <article class="rzf-carousel">
        <header class="rzf-carousel__header">
          <slot name="title"></slot>
          <slot name="subtitle"></slot>
        </header>
        <section class="rzf-carousel__main-slider">
          <slot name="slider" />
        </section>

        {this.config.navigation && (
          <button class="keen-slider__next" aria-label="Next slide">
            <slot name="next">Next</slot>
          </button>
        )}

        {this.config.navigation && (
          <button class="keen-slider__prev" aria-label="Previous slide">
            <slot name="prev">Prev</slot>
          </button>
        )}

        {this.config.pagination && <div class="rzf-carousel__dots"></div>}

        {this.config.showProgressBar && <slot name="progressBar"></slot>}

        <section class="rzf-carousel__content-slider">
          <slot name="content" />
        </section>
      </article>
    );
  }
}
